import { useEffect } from 'react';
import { Button } from '@design-system';
import { CheckCircle, XCircle, X } from 'lucide-react';

interface PuiSendConfirmationProps {
  emailResults: PostAnonymousPuiEmailResponse;
  onComplete: () => void;
}

export function PuiSendConfirmation({ emailResults, onComplete }: PuiSendConfirmationProps) {
  const { emailsSent, emailsFailed } = emailResults;
  const totalEmails = emailsSent.length + emailsFailed.length;
  const allSucceeded = emailsFailed.length === 0;
  const allFailed = emailsSent.length === 0;

  useEffect(() => {
    const timer = setTimeout(() => {
      onComplete();
    }, 10000);

    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div className="space-y-6 text-center">
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-2 top-2 h-8 w-8"
        onClick={onComplete}
      >
        <X className="h-4 w-4" />
      </Button>

      {/* Icon and Title */}
      <div className="flex flex-col items-center gap-4">
        {allSucceeded ? (
          <div className="rounded-full bg-green-100 p-4">
            <CheckCircle className="h-16 w-16 text-green-600" />
          </div>
        ) : allFailed ? (
          <div className="rounded-full bg-destructive/10 p-4">
            <XCircle className="h-16 w-16 text-destructive" />
          </div>
        ) : (
          <div className="rounded-full bg-yellow-100 p-4">
            <div className="flex gap-2">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <XCircle className="h-8 w-8 text-destructive" />
            </div>
          </div>
        )}

        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {allSucceeded
              ? 'Successfully Sent!'
              : allFailed
                ? 'Failed to Send'
                : 'Partially Sent'}
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            {allSucceeded
              ? `All ${totalEmails} email${totalEmails > 1 ? 's' : ''} sent successfully`
              : allFailed
                ? `Failed to send ${totalEmails} email${totalEmails > 1 ? 's' : ''}`
                : `${emailsSent.length} of ${totalEmails} emails sent`}
          </p>
        </div>
      </div>

      {/* Results Details */}
      <div className="space-y-3">
        {emailsSent.length > 0 && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
              <div className="flex-1 text-left">
                <h3 className="font-semibold text-green-900">
                  Successfully Sent ({emailsSent.length})
                </h3>
                <ul className="mt-2 space-y-1 text-sm text-green-800">
                  {emailsSent.map((email, index) => (
                    <li
                      key={index}
                      className="truncate"
                    >
                      {email}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {emailsFailed.length > 0 && (
          <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4">
            <div className="flex items-start gap-3">
              <XCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-destructive" />
              <div className="flex-1 text-left">
                <h3 className="font-semibold text-destructive">Failed to Send ({emailsFailed.length})</h3>
                <ul className="mt-2 space-y-1 text-sm text-destructive/90">
                  {emailsFailed.map((email, index) => (
                    <li
                      key={index}
                      className="truncate"
                    >
                      {email}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Auto-close message */}
      <p className="text-xs text-gray-500">This message will close automatically in 10 seconds</p>
    </div>
  );
}